import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  Text,
  Box,
  Button,
  Checkbox,
} from "@chakra-ui/react";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { MANDATORY_PERMISSION_SLUG } from "utils/permissions";

export type AccessRole = "basic" | "advanced";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  role: AccessRole;
  permissions?: IPermissionItem[];
  selectedIds: number[];
  setSelectedIds: Dispatch<SetStateAction<number[]>>;
};

const ROLE_LABEL: Record<AccessRole, string> = {
  basic: "Basic",
  advanced: "Advanced",
};

const transformName = (name: string) =>
  name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const AccessPermissionsModal = ({
  isOpen,
  onClose,
  role,
  permissions,
  selectedIds,
  setSelectedIds,
}: Props) => {
  const userRef = useRef(null);

  const handleCheckboxChange = (id: number) => {
    setSelectedIds((prevSelectedIds) => {
      const updatedSet = new Set(prevSelectedIds);
      if (updatedSet.has(id)) {
        updatedSet.delete(id);
      } else {
        updatedSet.add(id);
      }
      return Array.from(updatedSet);
    });
  };

  const mandatoryId = useMemo(
    () =>
      permissions?.find(
        (permission) => permission?.slug === MANDATORY_PERMISSION_SLUG
      )?.id,
    [permissions]
  );

  const toggleableIds = useMemo(
    () =>
      permissions
        ?.filter((permission) => permission?.id !== mandatoryId)
        .map((permission) => permission?.id) ?? [],
    [permissions, mandatoryId]
  );

  const isAllSelected =
    toggleableIds.length > 0 &&
    toggleableIds.every((id) => selectedIds?.includes(id));
  const isSomeSelected =
    !isAllSelected && toggleableIds.some((id) => selectedIds?.includes(id));

  const handleSelectAll = () => {
    const mandatory = mandatoryId ? [mandatoryId] : [];
    setSelectedIds(
      isAllSelected ? mandatory : [...mandatory, ...toggleableIds]
    );
  };

  useEffect(() => {
    if (!mandatoryId) return;

    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.includes(mandatoryId)
        ? prevSelectedIds
        : [...prevSelectedIds, mandatoryId]
    );
  }, [mandatoryId, setSelectedIds]);

  return (
    <Modal
      size="xl"
      finalFocusRef={userRef}
      isOpen={isOpen}
      onClose={onClose}
      isCentered={true}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <Box>
            <Text
              fontSize={`1.3rem`}
              fontWeight={`bold`}
              marginBottom={`2rem`}
              marginTop={`2rem`}
            >
              Add new Admin - {ROLE_LABEL[role]}
            </Text>
            <Box>
              <Box
                display={`flex`}
                justifyContent={`space-between`}
                alignItems={`center`}
                marginBottom={`2rem`}
                gap={`1rem`}
              >
                <Text fontSize={`1rem`} fontWeight={`w500`}>
                  Select access areas
                </Text>
                <Checkbox
                  isChecked={isAllSelected}
                  isIndeterminate={isSomeSelected}
                  isDisabled={toggleableIds.length === 0}
                  onChange={handleSelectAll}
                  fontWeight="600"
                  fontSize={`14px`}
                >
                  Select all access areas
                </Checkbox>
              </Box>
              <Box
                display={`grid`}
                gridTemplateColumns={`repeat(2, 1fr)`}
                marginBottom={`4rem`}
                gap={`2rem`}
              >
                {permissions?.map((permission) => {
                  const isMandatory =
                    permission?.slug === MANDATORY_PERMISSION_SLUG;

                  return (
                    <Checkbox
                      key={permission.id}
                      isChecked={
                        isMandatory || selectedIds?.includes(permission?.id)
                      }
                      isDisabled={isMandatory}
                      onChange={() => handleCheckboxChange(permission?.id)}
                      fontWeight="600"
                    >
                      {transformName(permission?.name)}
                    </Checkbox>
                  );
                })}
              </Box>

              <Button
                color={`#fff`}
                backgroundColor={`#00473C`}
                width={`100%`}
                borderRadius={`.5rem`}
                marginBottom={`1rem`}
                onClick={onClose}
              >
                Confirm Access areas
              </Button>
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AccessPermissionsModal;
